import { isAllowedClusterId } from "utils/clusterAccess";
import { decode } from "utils/HelperFunctions";
import { decodeLegacyUrlValue } from "utils/legacyUrlDecoder";
import type { PostLoginRoute } from "utils/postLoginRoute";

const DATABASE_ID_PATTERN = /^[1-9]\d{0,19}$/;
const ACCOUNT_ID_PATTERN = /^[A-Za-z0-9]{1,64}(?:-[A-Za-z0-9]{1,64})*$/;
const CANONICAL_CLUSTER_SLUG_PATTERN = /^ci_[A-Za-z0-9_-]{1,508}$/;
// Some historical update-billing links encoded an account-ID hyphen as 0xfd.
// This alias is deliberately route-specific; strict alphanumeric validation below
// prevents any other non-ASCII decoded value from entering the API payload.
const LEGACY_BILLING_BYTE_ALIASES = new Map([[0xfd, 0x2d]]);

export type LegacyUpdateBillingResolverRequest = {
  schedule_id: string;
  account_id: string;
};

export type LegacyUpdateBillingResolverArgs =
  LegacyUpdateBillingResolverRequest & {
    __abortSignal?: AbortSignal;
  };

type LegacyUpdateBillingResolverPayload = {
  success?: boolean;
  data?: {
    clusterSlug?: string;
  };
};

export type LegacyUpdateBillingResolverResponse =
  LegacyUpdateBillingResolverPayload & {
    body?: LegacyUpdateBillingResolverPayload;
  };

export type LegacyUpdateBillingRoute = {
  clusterId: string;
  clusterSlug: string;
  loginPath: string;
  postLoginRoute: PostLoginRoute;
};

export const parseLegacyUpdateBillingScheduleId = (
  encodedScheduleId: string | null | undefined,
  legacyCodes?: string
): LegacyUpdateBillingResolverRequest | null => {
  const decodedValue = decodeLegacyUrlValue(
    encodedScheduleId || undefined,
    legacyCodes,
    LEGACY_BILLING_BYTE_ALIASES
  );
  const separatorIndex = decodedValue?.indexOf("-") ?? -1;

  if (separatorIndex < 1 || !decodedValue) {
    return null;
  }

  const scheduleId = decodedValue.slice(0, separatorIndex);
  const accountId = decodedValue.slice(separatorIndex + 1);

  if (
    !DATABASE_ID_PATTERN.test(scheduleId) ||
    !ACCOUNT_ID_PATTERN.test(accountId)
  ) {
    return null;
  }

  return {
    schedule_id: scheduleId,
    account_id: accountId,
  };
};

export const getLegacyUpdateBillingRequestFromSearch = (
  search: string,
  legacyCodes?: string
): LegacyUpdateBillingResolverRequest | null => {
  const searchParams = new URLSearchParams(search);
  const scheduleIds = searchParams.getAll("schedule_id");

  return scheduleIds.length === 1
    ? parseLegacyUpdateBillingScheduleId(scheduleIds[0], legacyCodes)
    : null;
};

export const getLegacyUpdateBillingRoute = (
  response: LegacyUpdateBillingResolverResponse | undefined
): LegacyUpdateBillingRoute | null => {
  const payload = response?.body || response;
  const clusterSlug = payload?.data?.clusterSlug;

  if (
    payload?.success !== true ||
    !clusterSlug ||
    !CANONICAL_CLUSTER_SLUG_PATTERN.test(clusterSlug)
  ) {
    return null;
  }

  try {
    const clusterId = decode(clusterSlug, true);

    if (
      !DATABASE_ID_PATTERN.test(clusterId) ||
      !isAllowedClusterId(clusterId)
    ) {
      return null;
    }

    return {
      clusterId,
      clusterSlug,
      loginPath: `/${clusterSlug}/login`,
      postLoginRoute: {
        pathname: `/${clusterSlug}/subscriptions-plans`,
        clusterId,
      },
    };
  } catch {
    return null;
  }
};

type LegacyUpdateBillingSession = {
  isLogin: boolean;
  hasToken: boolean;
  accountId: unknown;
  clusterId: unknown;
};

export const hasMatchingLegacyUpdateBillingSession = (
  session: LegacyUpdateBillingSession,
  request: LegacyUpdateBillingResolverRequest,
  route: LegacyUpdateBillingRoute
): boolean =>
  session.isLogin &&
  session.hasToken &&
  String(session.accountId || "") === request.account_id &&
  String(session.clusterId || "") === route.clusterId;
